import React from 'react';
import {QueryGraphAction, QueryGraphEffect} from './interface';
import {isAction} from './determine-graph-action';

export function useActionHistory() {
    const [actionHistory, setActionHistory] = React.useState<
        Array<QueryGraphAction | QueryGraphEffect>
    >([]);
    const [pointerIndex, setPointerIndex] = React.useState(-1);

    const historyMoveBack = React.useCallback(() => {
        setPointerIndex((i) => i - 1);
    }, []);

    const historyMoveForward = React.useCallback(() => {
        setPointerIndex((i) => i + 1);
    }, []);

    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    const undoQueue = React.useMemo(() => {
        if (pointerIndex === -1) {
            return [];
        }

        let cursor = pointerIndex;
        const result: Array<QueryGraphAction | QueryGraphEffect> = [];

        while (!isAction(result[result.length - 1])) {
            if (cursor < 0) {
                return [];
            }

            result.push(actionHistory[cursor]);
            cursor--;
        }

        return result;
    }, [pointerIndex, actionHistory]);

    const redoQueue = React.useMemo(() => {
        if (pointerIndex === actionHistory.length - 1) {
            return [];
        }

        let cursor = pointerIndex + 1;
        const result: Array<QueryGraphAction | QueryGraphEffect> = [];

        do {
            result.push(actionHistory[cursor]);
            cursor++;
        } while (!isAction(actionHistory[cursor]) && cursor < actionHistory.length);

        return result;
    }, [pointerIndex, actionHistory]);

    const addToHistory = React.useCallback(
        (actionsOrEffects: Array<QueryGraphAction | QueryGraphEffect>) => {
            setActionHistory((prev) => [
                ...[...prev].slice(0, pointerIndex + actionsOrEffects.length),
                ...actionsOrEffects
            ]);
            setPointerIndex(pointerIndex + actionsOrEffects.length);
        },
        [pointerIndex]
    );

    const clearHistory = React.useCallback(() => {
        setActionHistory([]);
        setPointerIndex(-1);
    }, []);

    return {
        actionHistory,
        addToHistory,
        historyMoveBack,
        historyMoveForward,
        undoQueue,
        redoQueue,
        clearHistory,
        pointerIndex
    };
}
